/**
 * 从释义中提取中文关键词，并在释义 / 记忆句中高亮
 * 仅高亮「释义」与「记忆」中都出现的词，便于对应具体词义
 *
 * 规则：
 * - 支持单字词义（如 look→看、snow→雪）
 * - 释义中按「完整中文片段」高亮，避免「看」误伤「看起来」
 * - 记忆句中允许子串高亮（如「看」高亮于「看见100块！」）
 * - 长词优先，短词不与已匹配区间重叠
 */

export const MEAN_HIGHLIGHT_STYLE =
  'position:relative;color:#212121;background:#fff3b0;padding:2px 5px;border-radius:4px;'

const CN_CHAR = /[\u4e00-\u9fa5]/
const CN_RUN = /[\u4e00-\u9fa5]+/g
const MEAN_SPLIT = /[；;，,、/|（）()\n]+/
const POS_PREFIX = /^[a-zA-Z.&]+\s*/

export function escapeHtml (unsafe) {
  return String(unsafe || '').replace(/[&<"'>]/g, match => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[match]))
}

export function escapeRegExp (text) {
  return String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function plainText (text) {
  return String(text || '').replace(/<[^>]+>/g, '')
}

function isChineseChar (ch) {
  return !!ch && CN_CHAR.test(ch)
}

/** 从释义文本提取中文词组（含单字，长词优先） */
export function extractKeywordsFromMean (meanText) {
  if (!meanText) return []
  const text = plainText(meanText)
  const result = []
  const parts = text.split(MEAN_SPLIT).map(s => s.trim()).filter(Boolean)
  parts.forEach(part => {
    const cleaned = part.replace(POS_PREFIX, '').trim()
    const matches = cleaned.match(CN_RUN)
    if (matches) result.push(...matches)
  })
  const all = text.match(CN_RUN) || []
  all.forEach(m => result.push(m))
  return [...new Set(result)].sort((a, b) => b.length - a.length)
}

/**
 * 提取释义与记忆句中都出现的中文词（长词优先，去掉被更长词包含的短词）
 */
export function extractMatchedKeywords (meanText, exampleText) {
  if (!meanText || !exampleText) return []
  const examplePlain = plainText(exampleText)
  const meanPlain = plainText(meanText)
  if (!examplePlain || !meanPlain) return []

  const matched = []
  const parts = meanPlain.split(MEAN_SPLIT).map(s => s.trim()).filter(Boolean)
  parts.forEach(part => {
    const cleaned = part.replace(POS_PREFIX, '').trim()
    const phrases = cleaned.match(CN_RUN) || []
    phrases.forEach(phrase => {
      if (phrase && examplePlain.includes(phrase)) matched.push(phrase)
    })
  })

  const unique = [...new Set(matched)].sort((a, b) => b.length - a.length)
  return unique.filter((kw, i, arr) =>
    !arr.slice(0, i).some(longer => longer.includes(kw) && longer !== kw)
  )
}

/**
 * 在纯文本中高亮关键词
 * @param {string} mode 'style' | 'class'
 * @param {object} options
 * @param {boolean} options.exactChineseRun 为 true 时仅高亮「完整中文片段」等于关键词的位置
 */
export function highlightKeywords (text, keywords, mode = 'style', style = MEAN_HIGHLIGHT_STYLE, options = {}) {
  if (!text) return ''
  const plain = plainText(text)
  if (!keywords || !keywords.length) return escapeHtml(plain)

  const { exactChineseRun = false } = options
  const sorted = [...keywords].filter(Boolean).sort((a, b) => b.length - a.length)
  const ranges = []

  sorted.forEach(keyword => {
    let from = 0
    while (from <= plain.length - keyword.length) {
      const idx = plain.indexOf(keyword, from)
      if (idx === -1) break
      const end = idx + keyword.length
      const overlaps = ranges.some(r => idx < r.end && end > r.start)
      if (!overlaps) {
        if (exactChineseRun) {
          const before = idx > 0 ? plain[idx - 1] : ''
          const after = end < plain.length ? plain[end] : ''
          if (isChineseChar(before) || isChineseChar(after)) {
            from = idx + 1
            continue
          }
        }
        ranges.push({ start: idx, end })
      }
      from = idx + 1
    }
  })

  ranges.sort((a, b) => a.start - b.start)

  const wrap = (s) => mode === 'class'
    ? `<span class="hl">${escapeHtml(s)}</span>`
    : `<span style="${style}">${escapeHtml(s)}</span>`

  let html = ''
  let last = 0
  ranges.forEach(r => {
    html += escapeHtml(plain.slice(last, r.start))
    html += wrap(plain.slice(r.start, r.end))
    last = r.end
  })
  html += escapeHtml(plain.slice(last))
  return html
}

export function buildMeanAndExampleHtml (mean, example, options = {}) {
  const { mode = 'style', style = MEAN_HIGHLIGHT_STYLE } = options
  const keywords = extractMatchedKeywords(mean, example)
  return {
    keywords,
    processedMean: mean
      ? highlightKeywords(mean, keywords, mode, style, { exactChineseRun: true })
      : '',
    processedExample: example
      ? highlightKeywords(example, keywords, mode, style, { exactChineseRun: false })
      : ''
  }
}



// WEBPACK FOOTER //
// ./src/utils/wordHighlight.js